import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, PenSquare, BarChart3, ArrowRight, Check, X } from 'lucide-react';
import { PLATFORM_META } from '../lib/platforms';

/**
 * One-time animated welcome shown to users who haven't connected any account
 * yet. Three short steps, ending on a CTA to /dashboard/accounts. Dismissal is
 * remembered in localStorage so it never nags twice; it also naturally stops
 * appearing once the user connects an account (the parent gates on that).
 */

// Keyed by user id so dismissing it on one account doesn't suppress it for
// another account on the same browser (the original bug: a shared key meant
// testing with several accounts hid onboarding for all of them).
const storageKey = (userId: string) => `zp_onboarding_v1_${userId}`;

export function hasSeenOnboarding(userId: string): boolean {
  try {
    return localStorage.getItem(storageKey(userId)) === '1';
  } catch {
    return true; // if storage is blocked, don't nag
  }
}
function markSeen(userId: string) {
  try {
    localStorage.setItem(storageKey(userId), '1');
  } catch {
    /* ignore */
  }
}

const PLATFORMS_SHOWCASE = ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok', 'youtube'] as const;

type Step = {
  icon: typeof Link2;
  iconBg: string;
  iconColor: string;
  title: string;
  body: string;
};

const STEPS: Step[] = [
  {
    icon: PenSquare,
    iconBg: 'bg-brand-50',
    iconColor: 'text-brand-600',
    title: '¡Bienvenido a Zettapost! 👋',
    body: 'Programa y publica en todas tus redes desde un solo sitio. Te lo enseñamos en 30 segundos.',
  },
  {
    icon: Link2,
    iconBg: 'bg-accent-50',
    iconColor: 'text-accent-600',
    title: 'Conecta tus redes',
    body: 'El primer paso es enlazar tus cuentas. Instagram, Facebook, X, LinkedIn, TikTok y más — en un par de clics.',
  },
  {
    icon: BarChart3,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    title: 'Crea, programa y mide',
    body: 'Escribe una vez, publícalo en todas, programa con el calendario y sigue tus resultados en analítica.',
  },
];

export function OnboardingModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') finish();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function finish() {
    markSeen(userId);
    onClose();
  }
  function connectNow() {
    markSeen(userId);
    onClose();
    navigate('/dashboard/accounts');
  }

  const s = STEPS[step]!;
  const Icon = s.icon;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-onb-pop">
        {/* Skip */}
        <button
          onClick={finish}
          aria-label="Cerrar"
          className="absolute top-4 right-4 z-10 h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
        >
          <X size={16} />
        </button>

        {/* Animated hero */}
        <div className="bg-gradient-to-br from-brand-50 via-white to-accent-50 px-6 pt-10 pb-6 flex flex-col items-center text-center">
          {/* The icon re-mounts per step (key) so its pop animation replays. */}
          <div key={step} className="relative animate-onb-step">
            <span className={`inline-flex h-16 w-16 items-center justify-center rounded-2xl ${s.iconBg} ${s.iconColor}`}>
              <Icon size={30} />
            </span>
            {/* Pulse ring only on the "connect" step to draw the eye. */}
            {step === 1 && (
              <span className="absolute inset-0 rounded-2xl border-2 border-accent-300 animate-pulse-ring" />
            )}
          </div>

          {/* On the connect step, show the floating platform icons. */}
          {step === 1 && (
            <div className="mt-5 flex items-center justify-center gap-2.5">
              {PLATFORMS_SHOWCASE.map((p, i) => {
                const meta = PLATFORM_META[p];
                const PIcon = meta.icon;
                return (
                  <span
                    key={p}
                    className={i % 2 === 0 ? 'animate-float-slow' : 'animate-float-fast'}
                    style={{ animationDelay: `${i * 0.15}s` }}
                  >
                    <span
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl shadow-sm ring-1 ring-black/5"
                      style={{ backgroundColor: meta.color }}
                    >
                      <PIcon size={18} color="white" />
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Step content (re-mounts per step to replay the slide). */}
        <div key={`c${step}`} className="px-6 pb-6 text-center animate-onb-step">
          <h2 className="text-xl font-bold text-slate-900">{s.title}</h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
        </div>

        {/* Footer: progress dots + actions */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? 'w-5 bg-brand-600' : 'w-1.5 bg-slate-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {!isLast ? (
              <>
                <button
                  onClick={finish}
                  className="text-sm font-medium text-slate-400 hover:text-slate-600 px-2 py-2 transition"
                >
                  Saltar
                </button>
                <button
                  onClick={() => setStep((v) => v + 1)}
                  className="btn-primary gap-1.5"
                >
                  Siguiente <ArrowRight size={15} />
                </button>
              </>
            ) : (
              <button onClick={connectNow} className="btn-primary gap-1.5">
                <Check size={15} /> Conectar mi primera red
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
